import React from 'react'
import { DataGrid, } from '@mui/x-data-grid'
import { styled } from '@mui/material/styles'
import notFound from "../NotFoundData"
import { formatMoney } from "../../utils/amount"
import { computeOwed, isPartiallyCollected } from "../../utils/partialPayment"
import { convertServerToLocalTime, formatDateField } from "../../utils/date"
import { formatUserWithContactGroups } from "../../utils/assignees"
import StatusCell from "./StatusCell"
import { LuPencil } from "react-icons/lu"
import { useNavigate } from "react-router-dom"
import { useSelector } from 'react-redux'
import { describeParkedCharge, formatGatewayResponse, isNeverRetry, isParkedCharge } from "../../utils/gatewayResponse"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { PARKED_CHARGE_VISIBLE } from "../../config/featureFlagKeys"


// Local styled component removed in favor of global theme overrides

const TableList = ({ data, userRole }) => {
  const navigate = useNavigate()
  const parkedChargeVisible = useSelector(selectFeatureFlag(PARKED_CHARGE_VISIBLE))

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 50
    },
    {
      "field": "processedAt",
      "headerName": "Date",
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        return (
          <div className="w-full text-right">{convertServerToLocalTime(params?.row?.transactionData?.processedAt)}</div>
        )
      },
      "width": 100,
      sortable: false
    },
    // Each transaction IS a payment doc, so the plan linkage (payment # + due date) is
    // already in the payload — surfacing it lets an operator tie a charge back to the
    // schedule row it settled. Pmt# links to the client's Payments tab.
    {
      "field": "pmt",
      "headerName": "Pmt#",
      renderCell: (params) => {
        const userId = params?.row?.user?._id || params?.row?.user
        return (
          <button
            type="button"
            className="text-primary-main hover:underline cursor-pointer bg-transparent border-0 p-0"
            data-testid={`pmt-link-${params?.row?._id}`}
            onClick={() => userId && navigate(`/contact/${userId}?activeTab=payment`)}
          >
            {params?.row?.pmt ?? '—'}
          </button>
        )
      },
      "width": 70,
      sortable: false
    },
    {
      "field": "dueDate",
      "headerName": "Due Date",
      renderCell: (params) => {
        return (
          <div>{params?.row?.dueDate ? formatDateField(params.row.dueDate) : '—'}</div>
        )
      },
      "width": 100,
      sortable: false
    },
    {
      "field": "amount",
      "headerName": "Amount",
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        return (
          <div className="w-full text-right">{formatMoney(params?.row?.amount)}</div>
        )
      },
      "width": 120,
      "editable": false,
      sortable: false
    },
    {
      "field": "transactionId",
      "headerName": "Transaction ID",
      renderCell: (params) => {
        // No fallback to reasonText: the Gateway Response column carries the reason now.
        const value = params?.row?.transactionId || ''
        return (
          <div className="w-full text-wrap line-clamp-3" title={value}>{value}</div>
        )
      },
      "width": 200,
      "editable": false,
      sortable: false
    },
    {
      "field": "custStatus",
      "headerName": "Status",
      renderCell: (params) => {
        const row = params?.row
        const parked = parkedChargeVisible && isParkedCharge(row?.transactionData, row?.status?.value)
        return (
          <div className="flex flex-col justify-center">
            <StatusCell props={params} />
            {parked && (
              <span
                className="mt-1 inline-block px-2 py-0.5 rounded-full bg-warning-light text-warning-main text-xs"
                title="The last charge attempt was not confirmed. Check it with the gateway before charging again."
                data-testid={`parked-charge-${row?._id}`}
              >
                Needs Review
              </span>
            )}
            {Array.isArray(row?.settlesPayments) && row?.settlesPayments.length > 0 && (
              <span
                className="mt-1 inline-block px-2 py-0.5 rounded-full bg-info-light text-info-main text-xs"
                title={`Settles ${row?.settlesPayments.length} scheduled payment(s)`}
              >
                Settles {row?.settlesPayments.length}
              </span>
            )}
            {row?.settledByPayment && (
              <span
                className="mt-1 inline-block px-2 py-0.5 rounded-full bg-success-light text-success-main text-xs"
                title="Settled later by a payment link"
              >
                Settled later
              </span>
            )}
            {isPartiallyCollected({ ...row, status: row?.status?.value }) && (
              <>
                <span
                  className="mt-1 inline-block px-2 py-0.5 rounded-full bg-warning-light text-warning-main text-xs"
                  title={`${formatMoney(row.amountPaid)} already collected by a payment link`}
                >
                  Partially paid
                </span>
                <div className="text-text-secondary text-xs">
                  {formatMoney(computeOwed(row))} of {formatMoney(row.amount)}
                </div>
              </>
            )}
          </div>
        )
      },
      // flex so the badge column absorbs slack on wide panels; minWidth keeps it from
      // collapsing to MUI's 50px floor when the grid is crowded (which clipped the pills).
      flex: 1, minWidth: 170, sortable: false
    },
    {
      "field": "comment",
      "headerName": "Comment",
      renderCell: (params) => {
        return (
          <p className="truncate overflow-hidden text-ellipsis whitespace-normal line-clamp-4">
            {params?.row?.transactionData?.comment}
          </p>
        )
      },
      "width": 250,
      "editable": false
      , sortable: false
    },
    {
      "field": "gatewayResponse",
      "headerName": "Gateway Response",
      // Formatting moved to utils/gatewayResponse so the Payment Plan tab renders the identical
      // string — the whole point of showing it in both places is that they agree.
      renderCell: (params) => {
        const parked = parkedChargeVisible && isParkedCharge(params?.row?.transactionData, params?.row?.status?.value)
        const { headline, detail } = parked
          ? describeParkedCharge(params?.row?.transactionData)
          : formatGatewayResponse(params?.row?.transactionData)
        const danger = isNeverRetry(params?.row?.transactionData)
        return (
          <div className="" data-testid={`gateway-response-${params?.row?._id}`}>
            <div className={`font-bold truncate overflow-hidden text-ellipsis whitespace-normal line-clamp-3 ${danger ? 'text-error-main' : ''}`}>
              {headline}
            </div>
            <p className="truncate overflow-hidden text-ellipsis whitespace-normal line-clamp-3">
              {detail}
            </p>
            {danger && (
              <span className="mt-1 inline-block px-2 py-0.5 rounded-full bg-error-light text-error-main text-xs">
                Do not retry
              </span>
            )}
          </div>
        )
      },
      "width": 250,
      "editable": false
      , sortable: false
    },
    {
      "field": "gateway",
      "headerName": "Gateway",
      renderCell: (params) => {
        return (
          <div className="">{params?.row?.transactionData?.gateway}</div>
        )
      },
      // Gateway names are short and bounded ("Cardknox", "Sola", "Authorize.net"), so a
      // fixed width is honored — flex was making width:120 inert and collapsing it to 50px.
      "width": 130,
      "editable": false
      , sortable: false
    },
    {
      "field": "processedBy",
      "headerName": "Processed By",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => {
        return (
          <span className="truncate overflow-hidden text-ellipsis whitespace-normal line-clamp-3">
            {formatUserWithContactGroups({
              user: params?.row?.transactionData?.processedBy,
              userRole,
              isSystem: true
            })}
          </span>
        )
      },
      sortable: false
    },
    {
      "field": "action",
      "headerName": "Action",
      renderCell: (params) => {
        return (
          <div key={params?.row?._id} className="flex justify-between w-full pr-1.5">
            <div className="cursor-pointer text-text-secondary hover:text-text-primary" onClick={() => {
              navigate(`/transactions/${params?.row?._id}/edit/${params?.row?.user?._id}`)
            }}>
              <LuPencil size={20} />
            </div>
          </div>
        )
      },
      // Just a pencil icon; a fixed width clears the "ACTION" header without flex pinning
      // it to the 50px floor (which truncated the header to "ACT...").
      width: 80,
      sortable: false
    }
  ]

  return (
    <div className="overflow-x-auto rounded-xl border border-divider">
      <DataGrid
        sx={{
          border: 'none',
          // Sum of the column widths/minWidths; the parent overflow-x-auto scrolls the
          // grid instead of MUI squeezing flex columns to their 50px floor.
          minWidth: 1650,
          '& .MuiDataGrid-columnHeaders': {
            '.MuiButtonBase-root[aria-haspopup="menu"]': {
              display: 'none'
            }
          },
          '.MuiDataGrid-virtualScroller': {
            minHeight: data?.length === 0 ? 300 : ''
          }
        }}
        rowHeight={90}
        getRowId={(row) => row._id}
        columns={columns}
        slots={{
          noRowsOverlay: notFound,
        }}
        columnVisibilityModel={{
          id: false
        }}
        rows={data}
        checkboxSelection={false}

        hideFooterPagination={true}

        disableRowSelectionOnClick
        disableColumnSelector
        getRowClassName={(params) => params?.row?.status === 'Failed' ? 'bg-error-200' : ''}
      />
    </div>

  )
}

export default TableList
